import { createInstance, type i18n as I18nInstance } from 'i18next';
import en from './locales/en.json';
import ru from './locales/ru.json';
import uk from './locales/uk.json';

export type AppLocale = 'en' | 'ru' | 'uk';

// Хранится только standalone-язык: в embed язык приходит из `context.language`
// и локального выбора нет вовсе. Ключ оставлен прежним (`-standalone`) — переименование
// потеряло бы язык, уже выбранный пользователями.
const STORAGE_KEY = 'idea-calc-locale-standalone';
export const SUPPORTED_LOCALES: readonly AppLocale[] = ['en', 'ru', 'uk'];

function detectInitialLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) return stored as AppLocale;
  } catch (_) {
    /* localStorage недоступен — используем дефолт */
  }
  return 'ru';
}

// Свой инстанс вместо глобального i18n.init(): в Module Federation модуль живёт
// на странице платформы, и глобальный init затёр бы инстанс хоста, если хост
// тоже на i18next. По той же причине не подключаем initReactI18next — он ставит
// глобальный default для useTranslation (setI18n/setDefaults). До дерева инстанс
// доходит через <I18nextProvider>, а опции react читаются из i18n.options.react.
export function createI18nInstance(locale: AppLocale): I18nInstance {
  const instance = createInstance();
  instance.init({
    resources: {
      en: { translation: en },
      ru: { translation: ru },
      uk: { translation: uk },
    },
    lng: locale,
    fallbackLng: 'en',
    // Локали писались с одинарными скобками ({count}, {qty}) — переключаем
    // interpolation с дефолтных {{...}} на {...}, иначе плейсхолдеры выводятся буквально.
    interpolation: { escapeValue: false, prefix: '{', suffix: '}' },
    react: { useSuspense: false },
  });
  return instance;
}

// Standalone-инстанс один на вкладку: язык живёт в localStorage, и переключатель
// языка должен менять его в уже отрисованном дереве. В embed инстанс, наоборот,
// создаётся на каждый mount из context.language — локального хранения там нет.
let standaloneInstance: I18nInstance | null = null;

export function getStandaloneI18n(): I18nInstance {
  if (!standaloneInstance) standaloneInstance = createI18nInstance(detectInitialLocale());
  return standaloneInstance;
}

export function setLocale(locale: string): void {
  if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return;
  getStandaloneI18n().changeLanguage(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch (_) {
    /* localStorage недоступен — молча игнорируем */
  }
}
